import { useCallback, useRef, useState } from 'react';
import html2canvas from 'html2canvas-pro';
import { isElementScrollable, isRelativeUrl } from '../utils';
import useTracking from '../../hooks/useTracking';
import { EventActions, EventNames } from '../../../shared/constants/analytics';
import usePageName from './usePageName';

export const useWidgetDownload = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);

  const { trackEvent } = useTracking();
  const { pageName } = usePageName();

  const download = useCallback(
    (image: string, { name = 'img', extension = 'png' } = {}) => {
      const a = document.createElement('a');
      a.href = image;
      a.download = `${name}.${extension}`;
      a.click();
    },
    [],
  );

  // Converting image as base64 to render on canvas,
  // Enabled cors for cloudfront image
  const fetchImageAsBase64 = useCallback(
    async (url: string): Promise<string> => {
      try {
        const response = await fetch(url, { mode: 'cors' });
        const blob = await response.blob();
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Failed to fetch image:', error);
        return '';
      }
    },
    [],
  );

  const updateScrollableElements = (
    elementToStyle: HTMLElement,
    clonedElement: HTMLDivElement,
    currentParentWidth: any,
  ) => {
    clonedElement.style.width = `${currentParentWidth}px`;

    const { isScrollable, isScrollableX, isScrollableY } =
      isElementScrollable(elementToStyle);

    if (elementToStyle && isScrollable) {
      // If element scrollable on x axis then adding overflowAmount to widget
      if (isScrollableX) {
        const overflowAmount =
          elementToStyle.scrollWidth - elementToStyle.clientWidth;
        clonedElement.style.width = `${
          currentParentWidth + overflowAmount + 24
        }px`;
      }

      // If element scrollable on y axis then removing maxheight and height of widget
      if (isScrollableY) {
        elementToStyle.classList.add('!overflow-y-visible');
        clonedElement.style.width = `${currentParentWidth}px`;

        elementToStyle.querySelectorAll('*').forEach((el) => {
          const computedStyle = getComputedStyle(el);
          if (el.classList.contains('intl-widget-ignore-element')) return;

          if (
            computedStyle.maxHeight !== 'none' &&
            computedStyle.maxHeight !== '0px'
          ) {
            (el as HTMLElement).style.maxHeight = 'none';
          }

          if (
            computedStyle.height !== 'auto' &&
            computedStyle.height !== '0px'
          ) {
            (el as HTMLElement).style.height = 'auto';
          }
        });
      }

      // If both axis scrollable then again add overflowAmount, maxheight and height and height will remove from if
      // (isScrollableY) { code
      if (isScrollableX && isScrollableY) {
        const overflowAmount =
          elementToStyle.scrollWidth - elementToStyle.clientWidth;
        clonedElement.style.width = `${
          currentParentWidth + overflowAmount + 24
        }px`;
      }

      elementToStyle.classList.add('overflow-visible');
    }
  };

  const widgetDownload = useCallback(
    async (title: string, id?: any) => {
      if (!ref.current) return;

      const widgetName = title.toLowerCase().split(' ').join('_');

      trackEvent(EventNames.WellBeingDashboard, {
        eventLabel: title ? widgetName : id,
        eventValue: id,
        eventAction: EventActions.click,
        eventCategory: 'export_image',
        eventSource: pageName,
      });

      setLoading(true);
      const clonedElement = ref.current.cloneNode(true) as HTMLDivElement;
      clonedElement.classList.add('hidden-class');
      document.body.appendChild(clonedElement);

      clonedElement.querySelectorAll('[class*="line-clamp"]').forEach((el) => {
        const toRemove = Array.from(el.classList).filter(
          (cls) => cls.startsWith('line-clamp-') || cls === 'text-ellipsis',
        );
        toRemove.forEach((cls) => el.classList.remove(cls));
      });

      const elementToStyle = clonedElement.querySelector(
        '[class*="overflow"]',
      ) as HTMLElement;
      const currentParentWidth = parseFloat(
        getComputedStyle(ref.current).width,
      );

      if (elementToStyle) {
        elementToStyle
          .querySelectorAll('.truncate')
          .forEach((truncateElement) => {
            truncateElement.classList.remove('truncate');
          });

        updateScrollableElements(
          elementToStyle,
          clonedElement,
          currentParentWidth,
        );
      }

      // Calling base64 string from cloudfront src url
      const images = clonedElement.querySelectorAll('img');
      const promises = Array.from(images).map(async (img) => {
        const src = img.getAttribute('src');
        if (src && !isRelativeUrl(src)) {
          const base64Src = await fetchImageAsBase64(src);
          img.setAttribute('src', base64Src);
        }
      });

      await Promise.all(promises);

      try {
        // Chartjs render
        const originalCanvas = ref.current.querySelector(
          'canvas',
        ) as HTMLCanvasElement;
        const clonedCanvas = clonedElement.querySelector(
          'canvas',
        ) as HTMLCanvasElement;
        if (originalCanvas && clonedCanvas) {
          const originalContext = originalCanvas.getContext('2d');
          const clonedContext = clonedCanvas.getContext('2d');
          if (originalContext && clonedContext) {
            clonedCanvas.width = originalCanvas.width;
            clonedCanvas.height = originalCanvas.height;
            clonedContext.drawImage(originalCanvas, 0, 0);
          }
        }

        const canvas = await html2canvas(clonedElement, { useCORS: true });
        const img = canvas.toDataURL('image/png', 1.0);
        download(img);
      } catch (err) {
        console.error('Error generating canvas:', err);
      } finally {
        document.body.removeChild(clonedElement);
        setLoading(false);
      }
    },
    [fetchImageAsBase64, download],
  );

  return { widgetDownload, loading, ref };
};
